import { format, formatDuration, parseISO } from 'date-fns';

export const formatPhoneNumber = (val?: string) => {
  if (!val) return;

  const cleaned = val.replace(/\D/g, '').slice(-10);
  const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);

  if (match) {
    return `(${match[1]}) ${match[2]}-${match[3]}`;
  }

  return val;
};

export const capitalizeWord = (word: string) => {
  return word.charAt(0).toUpperCase() + word.slice(1);
};

/**
 * Normalizes a person's display name to title case, preserving hyphens.
 *
 * Healthie stores last names in ALL CAPS ("Gina TANTES", "Haley HARTWICK-EMMERSON").
 * This function capitalizes the first letter of every word and word-part (hyphen-
 * separated) and lowercases the rest, so names render consistently in the UI.
 *
 * "Alice SMITH"              → "Alice Smith"
 * "bob JONES"                → "Bob Jones"
 * "Jane SMITH-JONES"         → "Jane Smith-Jones"
 * "Maria DE LA CRUZ"         → "Maria De La Cruz"
 * "John DOE JR"              → "John Doe Jr"
 */
export const toDisplayName = (name: string): string => {
  try {
    return name
      .trim()
      .split(' ')
      .filter(Boolean)
      .map((word) =>
        word
          .split('-')
          .map(
            (part) =>
              part.charAt(0).toUpperCase() + part.slice(1).toLowerCase(),
          )
          .join('-'),
      )
      .join(' ');
  } catch {
    return name;
  }
};

// ---------------------------------------------------------------------------
// Numeric parsing — Healthie may return numbers or strings with unit suffixes
// ---------------------------------------------------------------------------

/**
 * Parse a numeric value from a number or string.
 * Handles Healthie's habit of returning values as `"160.0 Lbs"` or `"78"`.
 */
export const parseNumericValue = (
  value: number | string | null | undefined,
): number | null => {
  if (value == null) return null;
  if (typeof value === 'number') return value;
  const num = parseFloat(String(value));
  return isNaN(num) ? null : num;
};

/**
 * Parse a height value into inches. Accepts:
 * - a number (inches)
 * - a numeric string like `"78"` (inches)
 * - a formatted string like `"6'6"` or `"6' 6\""`
 */
export const parseHeightInches = (
  height: number | string | null | undefined,
): number | null => {
  if (height == null) return null;
  if (typeof height === 'number') return height;
  const str = String(height).trim();
  const ftInMatch = str.match(/^(\d+)['''ʼ\u2018\u2019]\s*(\d+)/);
  if (ftInMatch) {
    return parseInt(ftInMatch[1], 10) * 12 + parseInt(ftInMatch[2], 10);
  }
  const num = parseFloat(str);
  return isNaN(num) ? null : num;
};

// ---------------------------------------------------------------------------
// Display formatters
// ---------------------------------------------------------------------------

/** Format nonnegative decimal hours to the nearest minute; omit units for zero. */
export const formatHoursAsDuration = (hours: number): string => {
  const totalMinutes = Math.round(hours * 60);
  if (totalMinutes === 0) return hours > 0 ? 'less than 1 minute' : '0';
  return formatDuration({
    hours: Math.floor(totalMinutes / 60),
    minutes: totalMinutes % 60,
  });
};

/** Format ISO calendar dates as a compact month/day range without a timezone shift. */
export const formatShortDateRange = (start: string, end: string): string =>
  `${format(parseISO(start), 'MMM d')}–${format(parseISO(end), 'MMM d')}`;

/**
 * Format height for display in feet and inches (e.g. `5' 8"`).
 * Returns `'N/A'` for null/undefined to stay backward-compatible with
 * existing consumers that expect a string in all cases.
 */
export const formatHeight = (
  height: number | string | null | undefined,
): string => {
  const inches = parseHeightInches(height);
  if (inches == null) return 'N/A';
  const feet = Math.floor(inches / 12);
  const remaining = Math.round(inches % 12);
  return `${feet}' ${remaining}"`;
};

/**
 * Format weight in pounds (e.g. `175 lbs`).
 * Handles Healthie returning `"160.0 Lbs"` — parses the number first to
 * avoid double-suffix like `"160.0 Lbs lbs"`.
 */
export const formatWeight = (
  weight: number | string | null | undefined,
): string => {
  const lbs = parseNumericValue(weight);
  if (lbs == null) return 'N/A';
  const display = lbs % 1 === 0 ? String(lbs) : lbs.toFixed(1);
  return `${display} lbs`;
};

/**
 * Compute BMI from weight (lbs) and height (any accepted format).
 * Returns null when either input is missing or zero.
 */
export const computeBmi = (
  weight: number | string | null | undefined,
  height: number | string | null | undefined,
): number | null => {
  const lbs = parseNumericValue(weight);
  const inches = parseHeightInches(height);
  if (lbs == null || inches == null || inches === 0) return null;
  return Math.round((lbs / (inches * inches)) * 703);
};

/**
 * Format location data to display city and state in a consistent format.
 * Optionally appends a timezone abbreviation (e.g. `"Denver, CO (MT)"`).
 */
export const formatLocation = (
  location: {
    city: string | null;
    state: string | null;
  } | null,
  timezoneAbbr?: string | null,
): string => {
  if (!location) return 'N/A';

  const { city, state } = location;
  let result: string;
  if (city && state) result = `${city}, ${state}`;
  else if (city) result = city;
  else if (state) result = state;
  else return 'N/A';

  if (timezoneAbbr) result = `${result} (${timezoneAbbr})`;
  return result;
};

/**
 * Compute a person's age in whole years from a date-of-birth string.
 * Returns null if the date is unparseable.
 */
export const computeAge = (dob: string | null | undefined): number | null => {
  if (!dob) return null;
  // Use sanitizeHealthieDate if available at the call site for cross-browser
  // safety; here we accept a pre-parsed or ISO string.
  const date = new Date(dob);
  if (isNaN(date.getTime())) return null;
  const today = new Date();
  let age = today.getFullYear() - date.getFullYear();
  const m = today.getMonth() - date.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < date.getDate())) age--;
  return age;
};

export const formatDate = (
  dateString: string | null,
  dateFormat = 'MMM d, yyyy',
): string => {
  if (!dateString) return '—';
  try {
    return format(parseISO(dateString), dateFormat);
  } catch {
    return '—';
  }
};
